import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  LinearProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';
import { audiencesApi } from '../../services/audiences';
import type { SegmentCoverageRollup } from '@traaker/shared';

/** Coverage bar: brand-covered (green) / assortment-leaked (amber) / mentioned
 *  without a link (blue) / not-covered (grey). */
function CoverageBar({ r }: { r: SegmentCoverageRollup }) {
  const total = r.total || 1;
  const seg = (n: number, color: string) =>
    n > 0 ? <Box sx={{ width: `${(n / total) * 100}%`, bgcolor: color, height: 10 }} /> : null;
  return (
    <Box sx={{ display: 'flex', width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
      {seg(r.brandCovered, '#2e7d32')}
      {seg(r.assortmentLeaked, '#ed6c02')}
      {seg(r.mentioned, '#0288d1')}
      {seg(r.notCovered, '#bdbdbd')}
    </Box>
  );
}

/**
 * Results view for a completed measurement run (Phase 4b). Per-segment coverage
 * bars + score + top recommended products. Legend:
 *   brand-covered   = the retailer is named/linked in the answer
 *   assortment-leak = a product it stocks is recommended, but NOT via the retailer
 *   mentioned       = the retailer is named in text, but no buy link or product match
 *   not-covered     = neither
 */
export function MeasurementResults({
  brandId,
  audienceId,
  runId,
}: {
  brandId: string;
  audienceId: string;
  runId: string;
}) {
  const [rollups, setRollups] = useState<SegmentCoverageRollup[] | null>(null);

  useEffect(() => {
    audiencesApi.runRollups(brandId, audienceId, runId).then(setRollups).catch(() => setRollups([]));
  }, [brandId, audienceId, runId]);

  if (!rollups) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (rollups.length === 0) {
    return <Typography color="text.secondary">Aucun résultat.</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Legend color="#2e7d32" icon={<CheckCircleIcon sx={{ fontSize: 16 }} />} label="Marque recommandée" />
          <Legend color="#ed6c02" icon={<WarningIcon sx={{ fontSize: 16 }} />} label="Produit recommandé, mais pas via vous" />
          <Legend color="#0288d1" icon={<InfoIcon sx={{ fontSize: 16 }} />} label="Mentionné sans lien" />
          <Legend color="#bdbdbd" icon={<CancelIcon sx={{ fontSize: 16 }} />} label="Non couvert" />
        </Stack>
      </Paper>

      {rollups.map((r) => (
        <Card key={r.segmentId} variant="outlined">
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography fontWeight={600} sx={{ flex: 1 }}>
                {r.segmentName}
              </Typography>
              <Tooltip title="Score de visibilité moyen (présence + position)">
                <Chip
                  label={`Score ${r.avgScore}`}
                  color={r.avgScore >= 60 ? 'success' : r.avgScore >= 30 ? 'warning' : 'default'}
                  size="small"
                />
              </Tooltip>
            </Stack>
            <CoverageBar r={r} />
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {r.brandCovered} recommandé · {r.assortmentLeaked} fuite · {r.mentioned} mentionné · {r.notCovered} absent ({r.total} réponses)
              </Typography>
            </Stack>

            {r.topProducts.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Produits les plus recommandés
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Réponses</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {r.topProducts.map((p) => (
                      <TableRow key={p.productId}>
                        <TableCell>{p.name}</TableCell>
                        <TableCell align="right">{p.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function Legend({ color, icon, label }: { color: string; icon: React.ReactNode; label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: 0.5, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </Box>
      <Typography variant="caption">{label}</Typography>
    </Stack>
  );
}

/** Small inline progress bar for a running measurement. */
export function RunProgress({ completed, total }: { completed: number; total: number }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Analyse en cours… {completed}/{total} ({pct}%)
      </Typography>
      <LinearProgress variant="determinate" value={pct} />
    </Box>
  );
}
